import React, { useState, useEffect } from 'react';

const DISMISS_KEY = 'legalia.disclaimer.dismissed.v1';

/**
 * Reusable UPL disclaimer for all Legalia pages.
 * Variants: "banner" (prominent, dismissable), "inline" (small), "footer" (site-wide footer block)
 */
function LegalDisclaimer({ variant = 'banner' }) {
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined' || variant !== 'banner') return false;
    try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  useEffect(() => {
    if (variant !== 'banner' || !dismissed) return;
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch {}
  }, [dismissed, variant]);

  if (variant === 'banner' && dismissed) return null;

  if (variant === 'inline') {
    return (
      <p className="text-xs text-gray-400 italic mt-2">
        AI-generated legal information, not legal advice. No attorney-client relationship is created. Consult a licensed attorney before acting on this information.
      </p>
    );
  }

  if (variant === 'footer') {
    return (
      <div className="bg-amber-50 border-t border-amber-200 py-4 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm font-semibold text-amber-800">Legal Information Disclaimer</p>
          <p className="text-xs text-amber-700 mt-1 max-w-3xl mx-auto">
            Legalia is an AI-powered legal research and information tool. It does <strong>not</strong> provide legal advice, and no attorney-client relationship
            is created by using this service. Legalia is not a law firm and does not provide legal representation.
            AI-generated analysis may contain errors and should be independently verified.
            Always consult a licensed attorney before making legal decisions.
          </p>
        </div>
      </div>
    );
  }

  // Default: banner (dismissable)
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 mb-6 relative">
      <button
        onClick={() => setDismissed(true)}
        aria-label="Dismiss disclaimer"
        className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-md text-amber-600 hover:bg-amber-100 hover:text-amber-800 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <div className="flex items-start gap-3 pr-8">
        <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <div>
          <p className="text-sm font-semibold text-amber-800">Legal Information Disclaimer</p>
          <p className="text-xs text-amber-700 mt-1">
            This tool provides AI-generated legal <strong>information</strong>, not legal <strong>advice</strong>.
            No attorney-client relationship is created. Legalia is not a law firm.
            Consult a licensed attorney before making legal decisions based on these results.
          </p>
        </div>
      </div>
    </div>
  );
}

export default LegalDisclaimer;
